import { useCallback, useEffect, useMemo } from "react";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { SEARCH_PRODUCTS_DEFAULT_PAGE_SIZE } from "@/features/pathpick/features/search-products/constants";
import {
  PathPickCollection,
  PathPickMarketPriceBasis,
  PathPickProductOrderBy,
} from "@/features/pathpick/types";
import {
  CLEARED_PATH_PICK_RANGE_FILTER,
  parsePathPickRangeFilter,
  PATH_PICK_RANGE_FILTER_KEYS,
  PathPickRangeFilter,
} from "@/features/pathpick/utils/range-filter";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import usePathPickFilterableBrandIds from "@/features/pathpick/features/search-products/hooks/usePathPickFilterableBrandIds";
import { PathPickProductListTab } from "@/features/pathpick/features/search-products/types";

export interface PathPickProductFilter extends PathPickRangeFilter {
  brandId__in?: number[];
  pathradeCategory__in?: string[];
  query?: string;
  // 상품 유형(상품 묶음) — 상단 버튼과 상세 필터의 상품 유형 섹션이 같은 값을 공유한다.
  // 값끼리는 OR(합집합)이라 여러 개를 같이 고를 수 있고, 비어 있으면 `전체`다.
  collection__in?: PathPickCollection[];
  // 상품 리스트 탭("all"은 undefined) — 새로고침 시에도 유지되도록 쿼리스트링으로 관리한다.
  tab?: PathPickProductListTab;
  // 신청 이력이 있는 상품 제외 토글 (requestType__in=NONE으로 해석) — true일 때만 쿼리스트링에 유지한다.
  excludeProcessed?: boolean;
  excludeOrdered?: boolean;
  // 정렬 기준 — 디폴트(추천순)도 쿼리스트링/API에 항상 명시한다.
  orderBy?: PathPickProductOrderBy;
  // 비교 기준(크림·포이즌 공통) — 디폴트(입찰가 기준)도 쿼리스트링/API에 항상 명시한다.
  marketPriceBasis?: PathPickMarketPriceBasis;
  // 페이지네이션 — 필터와 한 저장소에 두어 (필터, 페이지)가 항상 함께 갱신되게 한다.
  // 로컬 state로 두면 둘을 맞추는 동기화 effect가 필요하고 새로고침에도 유지되지 않는다.
  // 디폴트(1페이지 / 기본 페이지 크기)는 쿼리스트링에서 생략한다.
  pageNumber?: number;
  pageSize?: number;
}

const DEFAULT_ORDER_BY: PathPickProductOrderBy = "recommended";
const DEFAULT_MARKET_PRICE_BASIS: PathPickMarketPriceBasis = "bidPrice";
const COLLECTIONS: PathPickCollection[] = [
  "HOT_DEAL",
  "KREAM_MATCHED",
  "POIZON_MATCHED",
  "FAVORITE_BRAND",
];
// 비교 표시를 끄면 노출되지 않는 상품 유형 — 걸려 있으면 뺀다.
const COMPARISON_COLLECTIONS: PathPickCollection[] = [
  "KREAM_MATCHED",
  "POIZON_MATCHED",
];
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = SEARCH_PRODUCTS_DEFAULT_PAGE_SIZE;

export type PathPickProductFilterKey = keyof PathPickProductFilter;

const FILTER_KEYS: PathPickProductFilterKey[] = [
  "brandId__in",
  "pathradeCategory__in",
  "query",
  "collection__in",
  ...PATH_PICK_RANGE_FILTER_KEYS,
  "excludeProcessed",
  "excludeOrdered",
  "orderBy",
  "marketPriceBasis",
  "pageNumber",
  "pageSize",
];

// 관리하는 모든 필터 키를 항상 포함(빈 값은 undefined)시켜 쿼리스트링 관리 키를 일관되게 유지한다.
function normalize(filter: PathPickProductFilter): PathPickProductFilter {
  return FILTER_KEYS.reduce((acc, key) => {
    const value = filter[key];
    if (typeof value === "boolean") {
      // boolean 필터는 true일 때만 쿼리스트링에 남긴다(false는 미적용과 동일).
      acc[key] = (value || undefined) as any;
    } else if (typeof value === "number") {
      acc[key] = value as any;
    } else {
      acc[key] = value && value.length > 0 ? (value as any) : undefined;
    }
    return acc;
  }, {} as PathPickProductFilter);
}

// 마진 정렬은 마진값 자체를 비교 기준으로 계산하므로, 기준이 바뀌면 순서가 재배치된다.
const isMarginOrderBy = (orderBy?: PathPickProductOrderBy) =>
  orderBy === "kream_margin_desc" ||
  orderBy === "kream_margin_asc" ||
  orderBy === "poizon_margin_desc" ||
  orderBy === "poizon_margin_asc";

// 거래 정렬은 체결건수/판매량 자체로 줄을 세우므로 비교 기준을 바꿔도 순서가 그대로다.
// (그래서 위 마진 판정과 합치지 않는다 — 기준 변경 시 페이지를 되돌릴 이유가 없다)
const isSalesOrderBy = (orderBy?: PathPickProductOrderBy) =>
  orderBy === "kream_sales_desc" ||
  orderBy === "kream_sales_asc" ||
  orderBy === "poizon_sales_desc" ||
  orderBy === "poizon_sales_asc";

// 비교 표시를 끄면 드롭다운에서 사라지는 정렬 — 마진·거래 모두 카드의 비교 블록에
// 그려지는 값(마진율/거래수)을 보고 고르는 정렬이라 함께 내려간다.
const isComparisonOrderBy = (orderBy?: PathPickProductOrderBy) =>
  isMarginOrderBy(orderBy) || isSalesOrderBy(orderBy);

const usePathPickProductFilter = () => {
  const [rawFilter, setFilter] = useTypedSearchParams<PathPickProductFilter>();
  // 비교 표시를 끄면 마진·거래 정렬과 매칭 상품 유형·범위 필터가 화면에서 사라진다. 그 값이 URL에 남아 있으면
  // 선택할 수 없는 조건으로 조회되므로(뒤로가기·직접 입력·설정 변경) 여기서 함께 떨어뜨린다.
  // 필터의 의미를 아는 곳이 한 군데뿐이어야 UI 옵션 목록과 API 요청이 어긋나지 않는다.
  const { isComparisonVisible } = usePathPickProductCardView();
  // 브랜드 필터도 같은 이유로 여기서 한 번 거른다 — 셀렉터 옵션(filter-info)에 없는 브랜드 id가
  // brandId__in에 실리면(인기·즐겨찾기 브랜드 카드 클릭 / 낡은 URL)
  // 체크를 해제할 방법이 없는 채로 칩에 브랜드명 대신 id가 그대로 노출되고 적용 개수에도 잡힌다.
  const filterableBrandIds = usePathPickFilterableBrandIds();

  // useTypedSearchParams는 값이 2개 이상인 숫자형 __in을 string[]로 파싱하는 한계가 있어
  // (공용 훅은 건드리지 않고) 여기서 brandId__in을 number[]로 정규화해 선언 타입 불변식을 복원한다.
  const filter = useMemo<PathPickProductFilter>(() => {
    // URL 주입 전 첫 렌더부터 디폴트를 사용해 이중 fetch를 막는다.
    const orderBy = rawFilter.orderBy ?? DEFAULT_ORDER_BY;
    const collections = rawFilter.collection__in?.filter(
      (collection) =>
        COLLECTIONS.includes(collection) &&
        (isComparisonVisible || !COMPARISON_COLLECTIONS.includes(collection))
    );
    const brandIds = rawFilter.brandId__in
      ?.map(Number)
      .filter((id) => filterableBrandIds.has(id));

    return {
      ...rawFilter,
      // 범위 값은 쿼리스트링에서 문자열로 들어오므로 숫자로 정규화한다.
      ...(isComparisonVisible
        ? parsePathPickRangeFilter(rawFilter)
        : CLEARED_PATH_PICK_RANGE_FILTER),
      collection__in:
        collections && collections.length > 0 ? collections : undefined,
      // 전부 걸러졌으면 "브랜드 미적용"과 같으므로 빈 배열 대신 undefined로 떨어뜨린다.
      brandId__in: brandIds && brandIds.length > 0 ? brandIds : undefined,
      orderBy:
        !isComparisonVisible && isComparisonOrderBy(orderBy)
          ? DEFAULT_ORDER_BY
          : orderBy,
      marketPriceBasis:
        rawFilter.marketPriceBasis ?? DEFAULT_MARKET_PRICE_BASIS,
    };
  }, [rawFilter, isComparisonVisible, filterableBrandIds]);

  const pageNumber = filter.pageNumber ?? DEFAULT_PAGE_NUMBER;
  const pageSize = filter.pageSize ?? DEFAULT_PAGE_SIZE;

  // 접속 시(그리고 전체 해제 후) 디폴트 정렬/비교 기준이 쿼리스트링에 항상 걸려 있도록 주입한다.
  // setter가 replace라 히스토리를 오염시키지 않고, 여러 인스턴스에서 실행돼도 멱등이다.
  useEffect(() => {
    if (
      rawFilter.orderBy === undefined ||
      rawFilter.marketPriceBasis === undefined
    ) {
      setFilter((prev) =>
        normalize({
          ...prev,
          orderBy: prev.orderBy ?? DEFAULT_ORDER_BY,
          marketPriceBasis: prev.marketPriceBasis ?? DEFAULT_MARKET_PRICE_BASIS,
        })
      );
    }
  }, [rawFilter.orderBy, rawFilter.marketPriceBasis, setFilter]);

  // 조회 결과 구성이 바뀌므로 첫 페이지로 되돌린다(페이지 크기는 사용자 설정이라 유지한다).
  // 아래 액션들이 pageNumber를 직접 명시하는 덕분에 필터와 페이지가 한 번의 URL 교체로 함께 갱신된다.
  const apply = useCallback(
    (next: PathPickProductFilter) => {
      setFilter(normalize({ ...next, pageNumber: undefined }));
    },
    [setFilter]
  );

  // 기존 필터는 유지한 채 검색어만 커밋한다.
  const applyQuery = useCallback(
    (query?: string) => {
      setFilter((prev) => normalize({ ...prev, query, pageNumber: undefined }));
    },
    [setFilter]
  );

  const clearAll = useCallback(() => {
    setFilter((prev) => normalize({ pageSize: prev.pageSize }));
  }, [setFilter]);

  // 비교 표시를 끈 직후 — 사라진 조건(마진·거래 정렬 / 매칭 탭)을 URL에서도 치운다.
  // 위 파생 filter가 이미 값을 떨어뜨려 화면·조회는 맞지만, URL에 남겨 두면 공유한 주소가
  // 실제로 보이는 목록과 달라진다.
  const resetComparisonFilters = useCallback(() => {
    setFilter((prev) =>
      normalize({
        ...prev,
        orderBy: isComparisonOrderBy(prev.orderBy)
          ? DEFAULT_ORDER_BY
          : prev.orderBy,
        collection__in: prev.collection__in?.filter(
          (collection) => !COMPARISON_COLLECTIONS.includes(collection)
        ),
        ...CLEARED_PATH_PICK_RANGE_FILTER,
        pageNumber: undefined,
      })
    );
  }, [setFilter]);

  // 상단 상품 유형 버튼 — 상세 필터와 달리 누르는 즉시 반영하고, 다른 필터는 그대로 둔다.
  const toggleCollection = useCallback(
    (collection: PathPickCollection) => {
      const current = filter.collection__in ?? [];
      apply({
        ...filter,
        collection__in: current.includes(collection)
          ? current.filter((item) => item !== collection)
          : [...current, collection],
      });
    },
    [filter, apply]
  );

  // 상단 `전체` 버튼 — 상품 유형 선택만 비운다.
  const clearCollections = useCallback(() => {
    apply({ ...filter, collection__in: undefined });
  }, [filter, apply]);

  const removeValue = useCallback(
    (key: PathPickProductFilterKey, value: string | number) => {
      // 정규화된 filter(숫자)로 비교/삭제해 2개 이상(원래 string[]) 상황에서도 정확히 제거한다.
      const current = (filter[key] ?? []) as (string | number)[];
      apply({
        ...filter,
        [key]: current.filter((item) => item !== value) as any,
      });
    },
    [filter, apply]
  );

  // 비교 기준은 마진 정렬일 때만 순서를 바꾸므로(마진값 자체가 이 기준으로 계산된다)
  // 그때만 첫 페이지로 되돌리고, 추천순/최신순/거래순에서는 보던 페이지를 유지한다
  // (표시되는 기준가·마진 값만 바뀌고 목록 구성은 그대로다).
  // 페이지를 조건부로 유지해야 해서 apply(항상 1페이지로 되돌린다)를 거치지 않는다.
  // 디폴트 정렬은 URL에서 생략될 수 있으므로 prev가 아니라 디폴트가 채워진 filter로 판단한다.
  const changeMarketPriceBasis = useCallback(
    (basis: PathPickMarketPriceBasis) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          marketPriceBasis: basis,
          pageNumber: isMarginOrderBy(filter.orderBy)
            ? undefined
            : prev.pageNumber,
        })
      );
    },
    [filter.orderBy, setFilter]
  );

  const setPageNumber = useCallback(
    (page: number) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          pageNumber: page === DEFAULT_PAGE_NUMBER ? undefined : page,
        })
      );
    },
    [setFilter]
  );

  const setPageSize = useCallback(
    (size: number) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          pageSize: size === DEFAULT_PAGE_SIZE ? undefined : size,
          pageNumber: undefined,
        })
      );
    },
    [setFilter]
  );

  return {
    filter,
    pageNumber,
    pageSize,
    apply,
    applyQuery,
    clearAll,
    resetComparisonFilters,
    toggleCollection,
    clearCollections,
    changeMarketPriceBasis,
    removeValue,
    setPageNumber,
    setPageSize,
  };
};

export default usePathPickProductFilter;
